import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchChatInbox,
  UnauthorizedError,
  type ChatInboxRow,
} from "@/lib/clinician-api";
import { useAuth } from "@/auth/AuthContext";
import { PatientLabel } from "@/components/PatientLabel";

// Chat split — the clinic-wide chat inbox: every conversation sorted by
// most-recent activity, with channel, patient shorthand, latest-message
// preview and the staff unread count (patient messages nobody has read).
// Clicking a row lands on the patient's chat pane. The backing endpoint
// existed since CAR-17 but had no UI until the split gave it channels.

const POLL_INTERVAL_MS = 15_000;

const CHANNEL_LABEL: Record<string, string> = {
  doctor: "Läkare",
  coach: "Coach",
};

function fmtTime(iso: string | null): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function senderLabel(senderType: string): string {
  switch (senderType) {
    case "patient":
      return "Patient";
    case "doctor":
      return "Läkare";
    case "coach":
      return "Coach";
    default:
      return "System";
  }
}

export function ChatInboxRoute() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [rows, setRows] = useState<ChatInboxRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;

  const load = useCallback(async () => {
    try {
      const { conversations } = await fetchChatInbox();
      setRows(conversations);
      setError(null);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOutRef.current();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
    const handle = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [load]);

  return (
    <div className="flex flex-col gap-md">
      <h1 className="font-display text-3xl tracking-tight">Chatt</h1>
      <p className="text-sm text-textSecondary max-w-2xl">
        Alla konversationer i kliniken, senast aktiva först. Olästa =
        meddelanden från patienten som ingen i teamet har öppnat.
      </p>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {rows && rows.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary">
          Inga konversationer än.
        </div>
      ) : null}

      <div className="flex flex-col gap-xs">
        {rows?.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() =>
              // TES-506 — land on the Meddelanden tab, in the channel that was
              // clicked. `#chat` alone never worked: the patient page kept its
              // tab in component state and read nothing from the location.
              navigate(`/patients/${c.patientId}?flik=chat&kanal=${c.channel}`)
            }
            className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-wrap items-center gap-md text-left hover:border-brand lg:flex-nowrap"
          >
            <span
              className={
                c.channel === "coach"
                  ? "px-sm py-xs rounded-pill text-xs bg-brand/10 text-brand"
                  : "px-sm py-xs rounded-pill text-xs bg-warning/15 text-warning"
              }
            >
              {CHANNEL_LABEL[c.channel] ?? c.channel}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm lg:w-56 lg:flex-none">
              <PatientLabel
                patientId={c.patientId}
                name={c.patientName}
                personalNumberLast6={c.personalNumberLast6}
              />
            </span>
            {/* TES-966 — on a phone the preview gets a line of its own. */}
            <span className="order-last basis-full text-sm truncate lg:order-none lg:flex-1 lg:basis-0">
              {c.latestMessage ? (
                <>
                  <span className="text-textSecondary">
                    {senderLabel(c.latestMessage.senderType)}:{" "}
                  </span>
                  {c.latestMessage.body}
                </>
              ) : (
                <span className="text-textSecondary">Inga meddelanden</span>
              )}
            </span>
            <span className="text-xs text-textSecondary">
              {fmtTime(c.lastMessageAt)}
            </span>
            {/* TES-889 (Ö-216) — "Väntat sedan": how long the oldest
                UNANSWERED patient message has been sitting. Unanswered, not
                unread — a thread somebody read and left is exactly what this
                exists to surface, and the unread badge beside it already
                answers the other question. */}
            {c.waitingSince ? (
              <span
                className={
                  c.delayed
                    ? "text-xs rounded-pill border border-danger/40 bg-danger/10 px-xs py-[2px] text-danger"
                    : "text-xs text-textSecondary"
                }
                title={
                  c.delayed
                    ? "Obesvarat i mer än 24 bemannade vardagstimmar"
                    : "Väntat sedan"
                }
              >
                {c.delayed ? "Försenad · " : "väntat sedan "}
                {fmtTime(c.waitingSince)}
              </span>
            ) : null}
            {c.unreadCount > 0 ? (
              <span className="min-w-[22px] h-[22px] px-xs rounded-full bg-brand text-white text-xs flex items-center justify-center">
                {c.unreadCount > 99 ? "99+" : c.unreadCount}
              </span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}
